"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OSDetector = void 0;
const WIN_11_PLATFORM_VERSION = 13;
/**
 * A utility class for detecting the operating system of the current environment.
 * It supports detection in both browser and Node.js environments.
 */
class OSDetector {
    /**
     * Detects the operating system of the current environment.
     *
     * @param options - Configuration options for the OS detection.
     * @returns A promise that resolves to the name of the detected operating system.
     *          If the OS cannot be determined, it returns "Unknown".
     */
    static getOS() {
        return __awaiter(this, arguments, void 0, function* (options = {}) {
            if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
                if (navigator.userAgentData) {
                    const os = yield this.detectWithUserAgentData(options);
                    if (os) {
                        return os;
                    }
                }
                const userAgent = navigator.userAgent.toLowerCase();
                if (options.returnUserAgent) {
                    return userAgent;
                }
                return this.detectFromUserAgent(userAgent);
            }
            if (typeof process !== 'undefined' && process.platform) {
                return this.detectInNode(options);
            }
            return 'Unknown';
        });
    }
    /**
     * Detects the operating system using the `navigator.userAgentData` API.
     *
     * @param options - Configuration options for the OS detection.
     * @returns A promise that resolves to the name of the detected operating system,
     *          or `null` if the detection fails.
     */
    static detectWithUserAgentData(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!navigator.userAgentData) {
                return null;
            }
            const { platform, platformVersion } = yield navigator.userAgentData.getHighEntropyValues(['platform', 'platformVersion']);
            const majorVersion = parseInt(platformVersion.split('.')[0], 10);
            if (platform === 'Windows' && majorVersion >= WIN_11_PLATFORM_VERSION) {
                if (options.returnUserAgent) {
                    return navigator.userAgent.replace('Windows NT 10.0', 'Windows NT 11.0');
                }
                return 'Windows 11';
            }
            return this.mapPlatform(platform);
        });
    }
    /**
     * Detects the operating system in a Node.js environment.
     *
     * @param options - Configuration options for the OS detection.
     * @returns The name of the detected operating system, or "Unknown (Node.js)" if it cannot be determined.
     */
    static detectInNode(options) {
        if (options.returnUserAgent) {
            return process.platform;
        }
        return this.detectFromPlatform(process.platform);
    }
    /**
     * Detects the operating system from the user agent string.
     *
     * @param userAgent - The user agent string to analyze.
     * @returns The name of the detected operating system, or "Unknown (Browser)" if it cannot be determined.
     */
    static detectFromUserAgent(userAgent) {
        for (const [key, value] of this.osMap) {
            if (userAgent.includes(key)) {
                return value;
            }
        }
        return 'Unknown (Browser)';
    }
    /**
     * Detects the operating system from the `process.platform` value in Node.js.
     *
     * @param platform - The platform string from `process.platform`.
     * @returns The name of the detected operating system, or "Unknown (Node.js)" if it cannot be determined.
     */
    static detectFromPlatform(platform) {
        const platformMap = new Map([
            ['win32', 'Windows'],
            ['darwin', 'MacOS'],
            ['linux', 'Linux'],
            ['freebsd', 'FreeBSD'],
            ['openbsd', 'OpenBSD'],
            ['sunos', 'SunOS'],
            ['aix', 'AIX'],
        ]);
        return platformMap.get(platform) || 'Unknown (Node.js)';
    }
    /**
     * Maps a platform string from `navigator.userAgentData` to a human-readable OS name.
     *
     * @param platform - The platform string from `navigator.userAgentData`.
     * @returns The name of the detected operating system, or "Unknown (Browser)" if it cannot be determined.
     */
    static mapPlatform(platform) {
        const platformMap = new Map([
            ['Windows', 'Windows'],
            ['MacOS', 'MacOS'],
            ['Linux', 'Linux'],
            ['Android', 'Android'],
            ['iOS', 'iOS'],
            ['Chrome OS', 'ChromeOS'],
        ]);
        return platformMap.get(platform) || 'Unknown (Browser)';
    }
}
exports.OSDetector = OSDetector;
/**
 * A mapping of user agent substrings to operating system names.
 * Used for detecting the OS from the user agent string.
 */
OSDetector.osMap = new Map([
    ['windows nt', 'Windows'],
    ['mac os x', 'MacOS'],
    ['linux', 'Linux'],
    ['android', 'Android'],
    ['iphone', 'iOS'],
    ['ipad', 'iOS'],
    ['cros', 'ChromeOS'],
    ['freebsd', 'FreeBSD'],
    ['openbsd', 'OpenBSD'],
    ['sunos', 'SunOS'],
    ['aix', 'AIX'],
]);
//# sourceMappingURL=osDetector.js.map